import { Drawer, Typography, Space, Card, Row, Col, Button, Alert, Divider, message, theme, Tag, Spin } from 'antd';
import { 
  AppstoreOutlined, 
  GlobalOutlined, 
  ClockCircleOutlined, 
  InfoCircleOutlined,
  CopyOutlined
} from '@ant-design/icons';
import { format } from 'date-fns';
import { StatusBadge } from './StatusBadge';
import { StepTimeline } from './StepTimeline';

const { Title, Text, Paragraph } = Typography;

export function AuditLogDetail({ log, open, onClose, loading = false }) {
  const { token } = theme.useToken();

  if (!log) return null;

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    message.success(`${label} copied to clipboard`);
  };

  const duration = log.completedAt
    ? new Date(log.completedAt).getTime() - new Date(log.startedAt).getTime()
    : null;

  return (
    <Drawer
      title={
        <Space>
          <span>Trace Details</span>
          <StatusBadge status={log.status} />
          {log.logName && <Tag>{log.logName}</Tag>}
        </Space>
      }
      open={open}
      onClose={onClose}
      width={640}
      styles={{ body: { paddingTop: 16 } }}
    >
      <Spin className="custom-spinner" spinning={loading} tip="Loading trace details...">
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Summary Cards */}
        <Row gutter={[12, 12]}>
          <Col span={12}>
            <Card size="small" style={{ background: token.colorBgTextHover }}>
              <Space>
                <AppstoreOutlined style={{ color: token.colorTextSecondary }} />
                <Text type="secondary" style={{ fontSize: 12 }}>Module</Text>
              </Space>
              <Paragraph strong style={{ margin: '4px 0 0' }}>{log.module || 'Unknown'}</Paragraph>
              <Text type="secondary" style={{ fontSize: 12 }}>{log.logName}</Text>
            </Card>
          </Col>

          <Col span={12}>
            <Card size="small" style={{ background: token.colorBgTextHover }}>
              <Space>
                <GlobalOutlined style={{ color: token.colorTextSecondary }} />
                <Text type="secondary" style={{ fontSize: 12 }}>Client Info</Text>
              </Space>
              <Paragraph strong style={{ margin: '4px 0 0' }}>{log.ipAddress || 'Unknown'}</Paragraph>
              <Text type="secondary" style={{ fontSize: 12 }}>{(() => {
                const info = log?.steps?.[0]?.request?.deviceInfo;
                if (!info) return 'Unknown device';
                return [info.browserName, info.browserVersion, info.deviceName, info.deviceVersion]
                  .filter(Boolean).join(', ') || 'Unknown device';
              })()}</Text>
            </Card>
          </Col>

          <Col span={12}>
            <Card size="small" style={{ background: token.colorBgTextHover }}>
              <Space>
                <ClockCircleOutlined style={{ color: token.colorTextSecondary }} />
                <Text type="secondary" style={{ fontSize: 12 }}>Duration</Text>
              </Space>
              <Paragraph strong style={{ margin: '4px 0 0' }}>
                {duration 
                  ? duration < 60000 
                    ? `${(duration / 1000).toFixed(0)}s` 
                    : `${Math.floor(duration / 60000)}m ${Math.floor((duration % 60000) / 1000)}s`
                  : 'In progress'}
              </Paragraph>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Started: {format(new Date(log.startedAt), 'HH:mm:ss')}
              </Text>
            </Card>
          </Col>

          <Col span={12}>
            <Card size="small" style={{ background: token.colorBgTextHover,height: '-webkit-fill-available' }}>
              <Space>
                <InfoCircleOutlined style={{ color: token.colorTextSecondary }} />
                <Text type="secondary" style={{ fontSize: 12 }}>Status</Text>
              </Space>
              <Paragraph strong style={{ margin: '4px 0 0' }}>
                {log.status} 
              </Paragraph>
              {/* <Text type="secondary" style={{ fontSize: 12 }}>
                Region: {log.meta?.region || 'Unknown'}
              </Text> */}
            </Card>
          </Col>
        </Row>

        {/* IDs Section */}
        <div>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
            Identifiers
          </Text>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Card size="small" style={{ background: token.colorBgTextHover }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 11 }}>Session ID</Text>
                  <Paragraph code style={{ margin: 0, fontSize: 13 }}>{log.correlationId}</Paragraph>
                </div>
                <Button 
                  type="text" 
                  icon={<CopyOutlined />} 
                  onClick={() => copyToClipboard(log.correlationId, 'Session ID')}
                />
              </div>
            </Card>
            
            {log.bookingRef && (
              <Card size="small" style={{ background: token.colorBgTextHover }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <Text type="secondary" style={{ fontSize: 11 }}>Record ID</Text>
                    <Paragraph code style={{ margin: 0, fontSize: 13 }}>{log.bookingRef}</Paragraph>
                  </div>
                  <Button 
                    type="text" 
                    icon={<CopyOutlined />} 
                    onClick={() => copyToClipboard(log.bookingRef, 'Record ID')}
                  />
                </div>
              </Card>
            )}
          </Space>
        </div>

        {/* Failed Step Alert */}
        {log.failedStep && (
          <Alert
            type="error"
            message={`Failed at: ${log.failedStep}`}
            description={log.steps.find(s => s.stepName === log.failedStep)?.errorMessage}
            showIcon
          />
        )}

        <Divider style={{ margin: '8px 0' }} />

        {/* Step Timeline */}
        <div>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 16 }}>
            Step Timeline ({log.steps.length} steps)
          </Text>
          <StepTimeline steps={log.steps} />
        </div>
      </Space>
      </Spin>
    </Drawer>
  );
}
